import { Component, Input, OnDestroy, inject } from '@angular/core';
import {
  ICoordenadas,
  IFilter,
  IQueryParam,
  Stop,
  Trip,
  IActivo,
  IReporteGenerico,
} from 'modelos/src';
import { Subscription } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import {
  LineStringMapa,
  MapaFacilData,
  MarkerMapa,
  Coordinate,
} from '../../../../../../auxiliares/mapa-facil/interfaces';
import { HelperService } from '../../../../../../auxiliares/servicios/helper.service';
import { MapaFacilService } from '../../../../../../auxiliares/mapa-facil/mapa-facil.service';
import { ReportesGenericosService } from '../../../../../../auxiliares/servicios/http/reportes-genericos.service';

@Component({
  selector: 'app-viajes-paradas',
  templateUrl: './viajes-paradas.component.html',
  styleUrl: './viajes-paradas.component.scss',
  standalone: false,
})
export class ViajesParadasComponent implements OnDestroy {
  private reportesGenericosService = inject(ReportesGenericosService);
  private helper = inject(HelperService);
  private mapaFacilService = inject(MapaFacilService);

  public loading = false;

  @Input() vehiculo?: IActivo;
  public viaje?: Trip;
  public parada?: Stop;

  public reportesSnappeados: [number, number][] = [];
  public reportes$?: Subscription;

  public mapaReady = false;
  public error?: string;

  public async listarViaje() {
    this.loading = true;
    if (!this.vehiculo) return;
    const desde = this.viaje?.startTime;
    const hasta = this.viaje?.endTime;
    if (!desde || !hasta) return;
    const filter: IFilter<IReporteGenerico> = {
      idsAsignados: this.vehiculo._id,
      fechaCreacion: { $gte: desde, $lte: hasta },
    };
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      limit: 0,
      sort: '-valores.timestamp',
      includeChildren: true,
    };

    try {
      const data =
        await this.reportesGenericosService.getReportesSnappeados(query);
      this.reportesSnappeados = data;
      this.mapaFacilService.clearParadas();
      this.mapaFacilService.updateData(this.armarViaje());
      this.loading = false;
    } catch (error: any) {
      this.helper.notifError(
        `Error al listar reportes del viaje: ${error?.error?.message}`,
      );
      console.error(error);
    }
  }

  private armarViaje() {
    if (!this.reportesSnappeados.length)
      throw new Error('No hay reportes para armar el viaje');

    // Mapbox usa [lon, lat] directamente
    const center: [number, number] = this.reportesSnappeados[0];
    const polylines: LineStringMapa[] = this.armarRecorridos();

    const viaje: MapaFacilData = {
      center,
      polylines,
      zoom: 14,
      geoMarker: 'Vehiculo',
    };
    return viaje;
  }

  private armarRecorridos() {
    const recorridos: LineStringMapa[] = [];
    const recorridoAsignado: LineStringMapa = this.armarRecorridoAsignado();

    // reportesSnappeados ya es [number, number][], no necesita conversión
    const coords: [number, number][] = this.reportesSnappeados;

    const recorrido: LineStringMapa = {
      points: coords,
      color: 'blue',
      width: 2,
      dash: true,
      // arrow: true,
      trip: true,
    };
    recorridos.push(recorrido);
    recorridos.push(recorridoAsignado);

    // Iniciar animación del trip usando Mapbox
    this.iniciarAnimacionTrip(coords);

    return recorridos;
  }

  /**
   * Inicia la animación del trip usando la nueva API de Mapbox
   */
  private iniciarAnimacionTrip(coords: [number, number][]): void {
    // Convertir las coordenadas a GeoJSON LineString
    const lineString: GeoJSON.Feature<GeoJSON.LineString> = {
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: coords,
      },
      properties: {},
    };

    // Iniciar animación con tipo 'Vehiculo' (colectivo)
    this.mapaFacilService.startTripAnimation(lineString, 'Vehiculo');
  }

  private armarRecorridoAsignado() {
    // Usar las coordenadas del geojson si existen
    const coords = this.vehiculo?.vehiculo.recorrido?.geojson?.coordinates;
    const recorrido = {
      points: coords,
      color: this.vehiculo?.vehiculo.recorrido?.color,
      width: 4,
    };
    return recorrido;
  }

  public async onViajeSelected(viaje: Trip) {
    try {
      this.viaje = viaje;
      // Uno solo al mismo tiempo
      this.parada = undefined;
      //
      this.error = undefined;
      this.mapaReady = false;
      await this.listarViaje();
      this.mapaReady = true;

      // Forzar resize del mapa después de mostrarlo (necesario para Mapbox)
      setTimeout(() => {
        const map = this.mapaFacilService.getMap();
        if (map) {
          map.resize();
        }
      }, 100);
    } catch (error) {
      const e = error as HttpErrorResponse;
      console.error(e);
      const mensaje = e.error.message;
      if (mensaje === 'Number of coordinates needs to be at least two.') {
        this.error = 'No hay suficientes reportes para armar el viaje';
      }
      if (mensaje === 'Could not find a matching segment for any coordinate.') {
        this.error =
          'No se encontró un segmento para niguna de las coordenadas';
      }
      this.viaje = undefined;
      this.mapaReady = false;
    }
  }

  ////// PARADA
  private armarParada() {
    if (!this.parada?.latitude || !this.parada?.longitude) {
      throw new Error('No hay coordenadas para armar la parada');
    }

    // Mapbox usa [lon, lat] directamente (sin conversión de OpenLayers)
    const coor: [number, number] = [
      this.parada.longitude,
      this.parada.latitude,
    ];

    const stop: MarkerMapa = {
      coor,
      label: 'parada',
      centrar: true,
    };
    const recorridoAsignado: LineStringMapa = this.armarRecorridoAsignado();
    const parada: MapaFacilData = {
      markers: [stop],
      polylines: [recorridoAsignado],
      zoom: 14,
    };
    this.mapaFacilService.clearVehiculos();
    this.mapaFacilService.updateData(parada);
  }

  public async onParadaSelected(parada: Stop) {
    try {
      this.parada = parada;
      // Uno solo al mismo tiempo
      this.viaje = undefined;
      //
      // Detener animación del trip si estaba corriendo
      this.mapaFacilService.stopTripAnimation();
      //
      this.error = undefined;
      this.mapaReady = false;
      this.armarParada();
      this.mapaReady = true;

      // Forzar resize del mapa después de mostrarlo (necesario para Mapbox)
      setTimeout(() => {
        const map = this.mapaFacilService.getMap();
        if (map) {
          map.resize();
        }
      }, 100);
    } catch (error) {
      const e = error as HttpErrorResponse;
      console.error(e);
      const mensaje = e.error.message;
      if (mensaje === 'Number of coordinates needs to be at least two.') {
        this.error = 'No hay suficientes reportes para armar el viaje';
      }
      if (mensaje === 'Could not find a matching segment for any coordinate.') {
        this.error =
          'No se encontró un segmento para niguna de las coordenadas';
      }
      this.parada = undefined;
      this.mapaReady = false;
    }
  }

  ngOnDestroy(): void {
    this.reportes$?.unsubscribe();
    // Detener animación al destruir el componente
    this.mapaFacilService.stopTripAnimation();
  }
}
